import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ArrangellyBrand from '@/components/brand/ArrangellyBrand';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const redirect = searchParams.get('redirect') || '';

  const handleRedirect = () => {
    // Always go back to home after login; user can manually go to dashboard from header
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // Small delay to ensure storage is set
        await new Promise((resolve) => setTimeout(resolve, 100));
        handleRedirect();
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Arrangelly</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-gradient-to-br from-slate-800/80 to-purple-900/20 border border-white/10 rounded-2xl p-8 backdrop-blur-md">
          <div className="flex justify-center mb-8">
            <ArrangellyBrand size="lg" layout="horizontal" variant="on-dark" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">Log in</h1>
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg bg-slate-900/60 border border-white/15 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg bg-slate-900/60 border border-white/15 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full mt-2 bg-accent hover:bg-accent/90 text-white disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Log in'}
            </Button>
            <p className="mt-3 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-purple-300 hover:text-purple-200 underline"
              >
                Forgot password?
              </Link>
            </p>
          </form>
          <p className="mt-6 text-xs text-gray-400 text-center">
            Don&apos;t have an account?{' '}
            <Link
              to={redirect ? `/signup?redirect=${redirect}` : '/signup'}
              className="text-purple-300 hover:text-purple-200 underline"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginPage;


